/**
 * RouteTransitionOverlay
 *
 * LUKHAS equivalent of Mont-Fort's `.hero-transition` VT class.
 *
 * Mont-Fort: A persistent, draggable overlay pre-loads all domain names.
 * Drag physics (friction=0.9, snapSpeed=2) snap between domains.
 * A GSAP timeline drives `--slide-progress` CSS var + WebGL camera curve.
 *
 * LUKHAS (React / Framer Motion):
 * - Persists at App root (outside <Routes>) — never remounted
 * - On route change: briefly shows the destination page label
 *   sliding in from the right, then fades out behind the new page
 * - The ΛS glyph and a 1px divider mirror Mont-Fort's diamond separators
 *
 * Cross-LUKHAS domain links (lukhas.ai / lukhas.id / lukhas.team) are
 * shown as secondary items — clicking navigates externally, just as
 * Mont-Fort transitions to capital.mont-fort.com etc.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import domainSkinsRaw from '../data/domainSkins.json'

/* ---------- route label map (mirrors Mont-Fort's en[] enum) --------- */
const ROUTE_LABELS: Record<string, string> = {
  '/': 'LUKHAS',
  '/solutions': 'Solutions',
  '/enterprise': 'Enterprise',
  '/governance': 'Governance',
  '/investors': 'Investors',
  '/contact': 'Contact',
  '/privacy': 'Privacy',
  '/terms': 'Terms',
  '/cookies': 'Cookies',
}

/* Cross-domain constellation links — equiv. of Mont-Fort's domain titles */
const CONSTELLATION_DOMAINS = [
  { label: 'LUKH\u039bS.AI', href: 'https://lukhas.ai', desc: 'Consciousness AI' },
  { label: 'LUKH\u039bS.ID', href: 'https://lukhas.id', desc: 'Identity & Access' },
  { label: 'LUKH\u039bS.TEAM', href: 'https://lukhas.team', desc: 'Team Portal' },
]

interface RouteSkin { label?: string }
const routeSkins = (domainSkinsRaw as { routes?: Record<string, RouteSkin> }).routes ?? {}

/* ---------- component ---------------------------------------------- */

export default function RouteTransitionOverlay() {
  const location = useLocation()
  const [visible, setVisible] = useState(false)
  const [label, setLabel] = useState('')
  const [showDomains, setShowDomains] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>()
  const prevPathname = useRef(location.pathname)

  /* Show overlay briefly on route change */
  useEffect(() => {
    if (location.pathname === prevPathname.current) return
    prevPathname.current = location.pathname

    const routeLabel =
      routeSkins[location.pathname]?.label ??
      ROUTE_LABELS[location.pathname] ??
      ''
    if (!routeLabel) return

    clearTimeout(hideTimer.current)
    setLabel(routeLabel)
    setVisible(true)
    hideTimer.current = setTimeout(() => setVisible(false), 1100)
    return () => clearTimeout(hideTimer.current)
  }, [location.pathname])

  /* lukhas:nav-change fallback (fired by SiteHeader on route change) */
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ pathname?: string }>).detail
      const pathname = detail?.pathname ?? location.pathname
      const routeLabel =
        routeSkins[pathname]?.label ?? ROUTE_LABELS[pathname] ?? ''
      if (!routeLabel || pathname === prevPathname.current) return
      clearTimeout(hideTimer.current)
      setLabel(routeLabel)
      setVisible(true)
      hideTimer.current = setTimeout(() => setVisible(false), 1100)
    }
    window.addEventListener('lukhas:nav-change', handler)
    return () => {
      window.removeEventListener('lukhas:nav-change', handler)
      clearTimeout(hideTimer.current)
    }
  }, [location.pathname])

  return (
    <>
      {/* ---- Route label flash ---- */}
      <AnimatePresence>
        {visible && (
          <motion.div
            key={label}
            aria-hidden="true"
            className="fixed inset-0 pointer-events-none z-40 flex items-center justify-center overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {/* Subtle mid-line — mirrors Mont-Fort's hero-transition :after rule */}
            <div
              className="absolute left-0 right-0 h-px"
              style={{
                top: '50%',
                background: 'rgba(59,130,246,0.12)',
              }}
            />
            <motion.p
              className="tracking-[0.32em] uppercase text-trust-blue/70 select-none"
              style={{
                fontFamily: '"Helvetica Neue", sans-serif',
                fontWeight: 100,
                fontSize: 'clamp(1rem, 3.5vw, 3.25rem)',
              }}
              initial={{ x: '8%', opacity: 0 }}
              animate={{ x: '0%', opacity: 1 }}
              exit={{ x: '-6%', opacity: 0 }}
              transition={{ duration: 0.55, ease: [0.23, 1, 0.32, 1] }}
            >
              {label}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- Cross-domain switcher trigger (bottom-left) ---- */}
      {/* Mirrors Mont-Fort's longpress to reveal .hero-transition */}
      <div className="fixed bottom-6 left-6 z-50 flex flex-col items-start gap-1">
        <button
          onClick={() => setShowDomains(v => !v)}
          className="group flex items-center gap-2 text-white/20 hover:text-trust-blue/60 transition-colors duration-300"
          aria-label="LUKHAS Constellation"
          style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}
        >
          {/* ΛS glyph — LUKHAS logo mark */}
          <span
            className="text-xs tracking-[0.3em]"
            style={{ fontSize: '0.65rem', letterSpacing: '0.3em' }}
          >
            &Lambda;S
          </span>
          {/* Diamond separator — mirrors Mont-Fort's .title::before */}
          <span
            className="inline-block w-1 h-1 bg-current"
            style={{ transform: 'rotate(45deg)', opacity: 0.5 }}
          />
          <span className="text-xs tracking-[0.2em] uppercase">Constellation</span>
        </button>

        <AnimatePresence>
          {showDomains && (
            <motion.ul
              className="flex flex-col gap-2 pl-1"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            >
              {/* Active domain — lukhas.com — shown with blue tint */}
              <motion.li
                key="lukhas.com"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -4 }}
                transition={{ duration: 0.25 }}
              >
                <span
                  className="flex items-baseline gap-2"
                >
                  <span
                    className="text-trust-blue/80 tracking-[0.2em] text-xs uppercase"
                    style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 100 }}
                  >
                    LUKH&Lambda;S.COM
                  </span>
                  <span className="text-trust-blue/40 text-[0.6rem] tracking-wider">
                    Corporate
                  </span>
                  <span
                    className="inline-block w-1 h-1 rounded-full bg-trust-blue/60"
                    style={{ marginLeft: '2px' }}
                  />
                </span>
              </motion.li>
              {CONSTELLATION_DOMAINS.map(({ label: domainLabel, href, desc }) => (
                <motion.li
                  key={href}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  transition={{ duration: 0.25 }}
                >
                  <a
                    href={href}
                    className="group/link flex items-baseline gap-2 no-underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowDomains(false)}
                  >
                    <span
                      className="text-white/40 group-hover/link:text-trust-blue/80 transition-colors duration-200 tracking-[0.2em] text-xs uppercase"
                      style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 100 }}
                    >
                      {domainLabel}
                    </span>
                    <span className="text-white/20 text-[0.6rem] tracking-wider">
                      {desc}
                    </span>
                  </a>
                </motion.li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
